import {AttrsSchema, Component} from 'panel';
import round from 'lodash/round';

import {commaizeNumber, htmlEncodeString} from 'common/util';
import {emitTrackEvent} from 'common/util/dom';
import {Section} from 'common/components/items-menu/types';
import {FeatureGate, RestrictEntitySaving} from 'common/report/constants';
import {featuresStore} from 'common/report/stores/features';
import {convertSegmentLabelForCohort, getCountTypeText} from 'common/report/util/funnels/utils';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {LineStyle} from 'common/components/line-chart/enums';
import {
  formatXAxis,
  formatYAxisLabel,
  getBarChartData,
  getBucketData,
  getFormattedUserCount,
  getFunnelStep,
  getLineChartDataforBucketReports,
  getToolTip,
  percentOfTotal,
  truncateBarChartDataForDownload,
} from 'common/report/models/funnels/bar-chart/utils';
import {
  conversionMode,
  FunnelStepBucketAccessors,
  getBucketLabel,
  getToolTipText,
  partialFormatter,
} from 'common/report/models/funnels/frequency-chart/utils';
import {
  BucketDefinition,
  ClickBarEvent,
  SelectedBar,
  SelectedSegmentStep,
} from 'common/report/models/funnels/bar-chart/types';
import {
  BasicFunnelStep,
  BucketChartVizType,
  CohortType,
  CountType,
  FunnelSegment,
  FunnelStep,
} from 'common/report/models/funnels/types';
import {FrequencyViewType} from 'common/report/models/funnels/frequency-chart/types';
import {DEFAULT_CHART_HEIGHT, ValueRepresentationType} from 'common/report/constants';
import {createBucketLineChartFunnelData} from 'common/report/models/funnels/utils';
import {ChartDisplayContext} from 'common/widgets/legend-line-chart';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import 'common/report/widgets/funnels/bar-chart';

import template from './index.jade';
import css from './index.styl?inline';

// TODO: Selection probably doesn't need to be maintained or should be maintained as something more agnostic to bar-chart+
interface ChartState {
  chartTooltip: TODO;
  popoverActive: boolean;
  selection: SelectedBar;
  funnelData: Map<string, SelectedSegmentStep>;
  comparisonData: Map<string, SelectedSegmentStep>;
}

interface ChartAttrs {
  'chart-view-type': FrequencyViewType;
  'chart-visualization': BucketChartVizType;
  'cohorts-allowed': boolean;
  'cohorts-create-disabled-message': string;
  'cohort-names': string;
  'data-group-id': string;
  'date-range-labels': string[];
  'display-context': ChartDisplayContext;
  'download-mode': boolean;
  'frequency-buckets': BucketDefinition;
  'funnels-count-type': CountType;
  'include-header': boolean;
  'is-compact': boolean;
  'legend-sections': Section[];
  'legend-theme': ChartLegendTheme;
  'representative-steps': Array<BasicFunnelStep | FunnelStep>;
  'segment-names': string[];
  'selected-segments': ColorChoice[];
  'should-show-median': boolean;
  'step-index': number;
  'value-representation': ValueRepresentationType;
}

export class FunnelsFrequencyChart extends Component<ChartState, ChartAttrs> {
  private _getCohortNameAndDescription: any;
  private _segmentData: FunnelSegment[] = [];
  private _comparisonSegmentData: FunnelSegment[] = [];

  /**
   * @property {boolean} cohorts-allowed - true if cohorts can be created from the chart
   * @property {string} cohort-names - names of existing cohorts as json string for popover validation
   * @property {string} data-group-id - the id of the currently selected data group
   * @property {json} segment-data - array of segmented steps for the funnel
   * @property {number} [step-index=null] step-index - index of step being viewed,
   *   default 0 since frequency should have picked steps
   * @property {json} frequency-buckets - request params for the frequency data
   * @property {number} frequency-buckets.bucket_size - size of buckets in seconds, default 0 to indicate no bucketing
   * @property {number} frequency-buckets.offset - offset from the start in seconds, default 0 for no offset
   */
  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      ...super.attrsSchema,
      'chart-view-type': {type: `string`, default: FrequencyViewType.Conversion},
      'chart-visualization': {type: `string`, default: BucketChartVizType.Bar},
      'cohorts-allowed': `boolean`,
      'cohorts-create-disabled-message': {type: `string`, default: ``},
      'cohort-names': {type: `string`},
      'data-group-id': {type: `string`, default: null},
      'date-range-labels': {type: `json`, default: []},
      'display-context': {
        type: `string`,
        enum: Object.values(ChartDisplayContext),
        default: ChartDisplayContext.Primary,
      },
      'download-mode': {type: `boolean`},
      'frequency-buckets': {type: `json`, default: {}},
      'funnels-count-type': {type: `string`, default: null},
      'include-header': `boolean`,
      'is-compact': `boolean`,
      'legend-sections': {type: `json`},
      'legend-theme': {type: `string`, enum: Object.values(ChartLegendTheme), default: ChartLegendTheme.Primary},
      'representative-steps': {type: `json`, default: []},
      'segment-names': {type: `json`, default: []},
      'selected-segments': {type: `json`, default: []},
      'should-show-median': `boolean`,
      'step-index': {type: `number`, default: 0},
      'value-representation': `string`,
    };
  }

  connectedCallback() {
    super.connectedCallback();
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        chartTooltip: null,
        popoverActive: false,
        selection: null,
        funnelData: null,
        comparisonData: null,
      },
      helpers: {
        ValueRepresentationType,
        isHidden: () => false,
        isConversionRate: () => this.isConversionRate(),
        hasData: () => {
          const stepData = this.segmentData?.[0]?.steps;
          if (!stepData || stepData.length === 0) {
            return false;
          }
          return this.conversionMode().bucketAccessor in stepData[0];
        },
        subtitleText: () => {
          const {bucketAccessor, representationText} = this.conversionMode();
          const funnelStep = getFunnelStep(this.attr(`representative-steps`), this.stepIndex);
          const bucketData = getBucketData(funnelStep, bucketAccessor);
          const overallConversionRatio = this.attr(`representative-steps`)[this.attr(`representative-steps`).length - 1]
            .overall_conv_ratio;
          const conversionNumber = this.isConversionRate()
            ? (overallConversionRatio * 100).toFixed(2) + `%`
            : getFormattedUserCount(bucketData);

          return {
            conversionText: `${conversionNumber} ${representationText}`,
            totalInStepText: `${commaizeNumber(funnelStep.count)} total`,
          };
        },
        showBarChart: () => {
          return this.attr(`chart-visualization`) === BucketChartVizType.Bar;
        },
        passThrough: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },

        /** Bar Chart Methods */
        chartLegendAttrs: () => {
          let height = `calc(100vh - 123px)`;
          if (!this.helpers.showBarChart()) {
            height = `${DEFAULT_CHART_HEIGHT}px`;
          }

          return {
            height,
            sections: JSON.stringify(this.attr(`legend-sections`)),
            'enable-search': true,
            'show-select-all': true,
            theme: this.attr(`download-mode`) ? ChartLegendTheme.ReadOnly : ChartLegendTheme.Primary,
          };
        },
        getChartToolTipAttrs: (sections, color, _) => {
          return {
            sections: JSON.stringify(sections),
            ...(!this.hasComparison() && {'title-color': color}),
          };
        },
        barChartData: () => {
          const overallStep = this.attr(`representative-steps`)
            ? getFunnelStep(this.attr(`representative-steps`), this.stepIndex)
            : null;
          const segmentData = [];
          this.funnelData.forEach((value, key) => {
            if (this.comparisonData) {
              segmentData.push(this.comparisonData.get(key));
            }
            segmentData.push(value);
          });

          const {bucketAccessor, medianAccessor} = this.conversionMode();

          const data = getBarChartData({
            segmentData,
            overallStep,
            selectedBucket: this.attr(`frequency-buckets`),
            selection: this.state.selection,
            bucketAccessor,
            medianAccessor,
            labelFormatter: partialFormatter,
            showAnnotation: this.attr(`should-show-median`),
            bucketClickMinimum: 1,
            showCountAsPercentage: this.isConversionRate(),
          });

          if (this.attr(`download-mode`)) {
            return truncateBarChartDataForDownload(data);
          } else {
            return {data, isTruncated: false};
          }
        },
        hoverBar: ({detail}) => {
          const {isAnnotation, bucket, segmentIndex, x, y} = detail;

          const {color, label, name} = bucket.segments[segmentIndex];
          const left = x - this.getBoundingClientRect().left + 20;
          const top = y - this.getBoundingClientRect().top + 10;
          const {step} = this.funnelData.get(name);
          const labelFormatter = {
            ...partialFormatter,
            getToolTipText: (count, percent, bucketLabel) => {
              return getToolTipText(
                count,
                percent,
                bucketLabel,
                !(bucket.start === 1 && this.attr(`frequency-buckets`).bucketSize === 1),
                step.step_label,
                this.attr(`chart-view-type`),
              );
            },
          };
          this.update({
            chartTooltip: {
              color,
              label,
              sections: getToolTip(
                bucket.label,
                name,
                bucket.queryIndex,
                isAnnotation,
                labelFormatter,
                this.funnelData,
                this.comparisonData,
                this.conversionMode().bucketAccessor,
                this.conversionMode().medianAccessor,
                this.attr(`date-range-labels`),
                this.isConversionRate() ? FunnelStepBucketAccessors.ConvertStepFreqBuckets : null,
                this.isConversionRate(),
              ),
              left,
              top,
            },
          });
        },
        offHoverBar: () => this.update({chartTooltip: null}),
        clickBar: (ev: ClickBarEvent) => {
          const {left, top} = this.getBoundingClientRect();
          const {clientX, clientY} = ev.detail.original;
          this.update({
            chartTooltip: null,
            popoverActive: true,
            selection: {...ev.detail, clientX: clientX - left, clientY: clientY - top},
          });
          this.dispatchEvent(new CustomEvent(`openPopover`));
        },

        /** Line Chart Methods */
        isSegmented: () => this.segmentData?.length > 1,
        lineChartData: () => {
          const {bucketAccessor} = this.conversionMode();
          let chartData = getLineChartDataforBucketReports({
            segmentData: this.segmentData,
            stepIndex: this.stepIndex,
            bucketAccessor: bucketAccessor as keyof FunnelStep,
            segmentColors: this.attr(`selected-segments`),
            isComparison: false,
            representationType: this.attr(`value-representation`),
            showCountAsPercentage: this.isConversionRate(),
          });
          if (this.hasComparison()) {
            chartData = [
              ...chartData,
              ...getLineChartDataforBucketReports({
                segmentData: this._comparisonSegmentData,
                stepIndex: this.stepIndex,
                bucketAccessor: bucketAccessor as keyof FunnelStep,
                segmentColors: this.attr(`selected-segments`),
                isComparison: true,
                representationType: this.attr(`value-representation`),
                showCountAsPercentage: this.isConversionRate(),
              }),
            ];
          }
          return chartData;
        },
        lineChartClick: (series: any, index: number, color: string, clientX: number, clientY: number) => {
          if (series.options.dashStyle === LineStyle.Comparison) {
            return;
          }
          const {name: segmentKey} = series;
          const {step: funnelStep, segmentName} = this.funnelData.get(segmentKey);
          const bucketData = getBucketData(funnelStep, this.conversionMode().bucketAccessor);
          const buckets = bucketData.buckets;
          const count = buckets[index];
          this.update({
            chartTooltip: null,
            popoverActive: true,
            selection: {
              segment: {
                color,
                count,
                percentage: percentOfTotal(bucketData, count),
                isSelected: true,
                isClickable: true,
                label: segmentName,
                name: segmentKey,
              },
              rowLabel: formatXAxis(index, this.attr(`frequency-buckets`), getBucketLabel, true),
              queryIndex: index,
              clientX,
              clientY,
            },
          });
        },
        formatTooltip: (x: number, _y: number, series: any, _idx: number) => {
          const labelFormatter = {
            ...partialFormatter,
            getToolTipText: (count, percentage, bucketLabel) =>
              getToolTipText(
                count,
                percentage,
                bucketLabel,
                !(x === 1 && this.attr(`frequency-buckets`).bucketSize === 1),
                getFunnelStep(this.attr(`representative-steps`), this.stepIndex).step_label,
                this.attr(`chart-view-type`),
              ),
          };

          const {color, name: seriesName} = series;
          let titleColor = ``;

          if (!this.hasComparison()) {
            titleColor = `title-color=${color}`;
          }
          const isCompact = this.attr(`is-compact`) ? `is-compact` : ``;
          const {bucketAccessor, medianAccessor} = this.conversionMode();
          const toolTipAttrs = `${titleColor} ${isCompact} sections="${htmlEncodeString(
            JSON.stringify(
              getToolTip(
                formatXAxis(x, this.attr(`frequency-buckets`), getBucketLabel, true),
                seriesName,
                x,
                false,
                labelFormatter,
                this.state.funnelData,
                this.state.comparisonData,
                bucketAccessor,
                medianAccessor,
                this.attr(`date-range-labels`),
                this.isConversionRate() ? FunnelStepBucketAccessors.ConvertStepFreqBuckets : null,
                this.isConversionRate(),
              ),
            ),
          )}"`;

          return `
                <mp-chart-tooltip ${toolTipAttrs}>
              `;
        },
        formatXAxisLabel: (value: number) => {
          return formatXAxis(value, this.attr(`frequency-buckets`), getBucketLabel);
        },
        formatYAxisLabel: (value: number) => {
          return formatYAxisLabel(value, this.attr(`value-representation`));
        },

        /** Popover Methods */
        popover: {
          handleCancelCohort: (ev) => {
            this.helpers.popover.closePopover();
            this.helpers.passThrough(ev);
          },
          popoverTitle: () => {
            const {selection} = this.state;
            return selection ? selection.segment.label : null;
          },
          popOverBodyForConversionRate: () => {
            const {selection} = this.state;
            if (selection) {
              const {
                segment: {count, name},
              } = selection;
              const funnelStep = this.funnelData.get(name as string).step;
              const conversionData = getBucketData(funnelStep, FunnelStepBucketAccessors.ConvertStepFreqBuckets);
              const dropOffData = getBucketData(funnelStep, FunnelStepBucketAccessors.DropoffStepFreqBuckets);
              const funnelConversionText = this.conversionMode().representationText;
              return [
                {
                  usersDroppedOff: `${dropOffData.buckets[selection[`rowIndex`]]}  users dropped off`,
                  usersConverted: `${conversionData.buckets[selection[`rowIndex`]]}  users converted`,
                  percent: `${round(count * 100, 2)}% ${funnelConversionText}`,
                  subText: `${selection.rowLabel} times`,
                },
              ];
            }
            return null;
          },
          popoverBody: () => {
            const {selection} = this.state;
            if (selection) {
              const {
                segment: {count, name},
              } = selection;
              const funnelStep = this.funnelData.get(name as string).step;
              const bucketData = getBucketData(funnelStep, this.conversionMode().bucketAccessor);
              const countTypeText = getCountTypeText({
                funnelsCountType: this.attr(`funnels-count-type`),
                dataGroupId: this.attr(`data-group-id`),
              });
              const funnelConversionText = this.conversionMode().representationText;
              return [
                {
                  count: `${commaizeNumber(count)} ${countTypeText}`,
                  percent: `${percentOfTotal(bucketData, count)}% ${funnelConversionText}`,
                  subText: `${selection.rowLabel} times`,
                },
              ];
            }
            return null;
          },
          buttonOptions: () => {
            const options = [];
            if (this.attr(`cohorts-allowed`)) {
              const disabledCreateCohortButtonMessage =
                this.state.selection.segment.name === `$other`
                  ? `Creating cohorts is disabled for $other segment`
                  : this.attr(`cohorts-create-disabled-message`);

              if (
                disabledCreateCohortButtonMessage ||
                featuresStore.value.canSaveEntity(RestrictEntitySaving.Cohorts)
              ) {
                const disableCohortProperties = disabledCreateCohortButtonMessage
                  ? {tooltip: disabledCreateCohortButtonMessage, isDisabled: true}
                  : {};

                if (this.isConversionRate()) {
                  options.push({
                    label: `Create cohort from conversion`,
                    value: CohortType.FREQUENCY_CONVERSION,
                    ...disableCohortProperties,
                  });

                  options.push({
                    label: `Create cohort from drop off`,
                    value: CohortType.FREQUENCY_DROP_OFF,
                    ...disableCohortProperties,
                  });
                } else {
                  options.push({
                    label: `Create Cohort`,
                    value:
                      this.attr(`chart-view-type`) === FrequencyViewType.Conversion
                        ? CohortType.FREQUENCY_CONVERSION
                        : CohortType.FREQUENCY_DROP_OFF,
                    ...disableCohortProperties,
                  });
                }
              } else {
                options.push({
                  label: `Upgrade to Create Cohort`,
                  icon: `share`,
                  value: `openUpsell`,
                  tooltip: `When you upgrade your plan, cohorts can be created and saved to use across all reports in a project.`,
                });
              }
            }
            return options;
          },
          closePopover: () => this.update({popoverActive: false, selection: null}),
          handlePopoverAction: ({detail: {action, value}}) => {
            const {selection} = this.state;
            if (action === `createCohort`) {
              // TODO: Add segment information to cohort creation flow
              this.el.dispatchEvent(
                new CustomEvent(`createCohort`, {
                  composed: true,
                  detail: {
                    bucket: selection.queryIndex,
                    index: this.stepIndex,
                    fromStart: false,
                    name: value.name,
                    description: value.description,
                    groupByValue: selection.segment.name,
                    type: value.type,
                    shouldShare: value.shouldShare,
                  },
                }),
              );
            } else if (action === `openUpsell`) {
              emitTrackEvent(this.el, {
                eventName: `Click Upsell`,
                props: {
                  'Feature Limit': FeatureGate.RestrictEntitySaving,
                  'Entity Type': `cohorts`,
                },
              });
              window.open(`https://mixpanel.com/pricing/`, `_blank`).focus();
            } else {
              throw new Error(`unsupported action`);
            }
          },
          /** place the popover 5px below the clicked bar */
          getPopoverPlacement: () => {
            if (!this.state.selection) {
              return null;
            }

            const {clientX, clientY} = this.state.selection;
            return {
              // align to the click position horizontally
              left: `${clientX}px`,
              // align to beneath the bar vertically
              top: `${clientY + 10}px`,
            };
          },
          cohortDetails: () => {
            const {selection} = this.state;
            if (!selection) {
              return [];
            }

            return [
              {
                type: CohortType.FREQUENCY_DROP_OFF,
                ...this.getCohortDetails({
                  type: CohortType.FREQUENCY_DROP_OFF,
                  conversionRange: selection.rowLabel,
                  segment: convertSegmentLabelForCohort(selection.segment.label, this.attr(`segment-names`)),
                  eventIndex: this.stepIndex,
                }),
              },
              {
                type: CohortType.FREQUENCY_CONVERSION,
                ...this.getCohortDetails({
                  type: CohortType.FREQUENCY_CONVERSION,
                  conversionRange: selection.rowLabel,
                  segment: convertSegmentLabelForCohort(selection.segment.label, this.attr(`segment-names`)),
                  eventIndex: this.stepIndex,
                }),
              },
            ];
          },
        },
      },
    };
  }

  hasComparison() {
    return this._comparisonSegmentData?.length > 0;
  }

  createData(segmentData: FunnelSegment[], isComparisonData = false): Map<string, SelectedSegmentStep> {
    return createBucketLineChartFunnelData({
      segmentData,
      selected: this.attr(`selected-segments`),
      stepIndex: this.stepIndex,
      hasComparison: this.hasComparison(),
      isComparisonData,
    });
  }

  get funnelData(): Map<string, SelectedSegmentStep> {
    return this.state.funnelData;
  }

  get comparisonData(): Map<string, SelectedSegmentStep> {
    return this.state.comparisonData;
  }

  set segmentData(val: FunnelSegment[]) {
    this._segmentData = val;
    this.update({funnelData: this.createData(val)});
  }

  get segmentData(): FunnelSegment[] {
    return this._segmentData;
  }

  set comparisonSegmentData(val: FunnelSegment[]) {
    this._comparisonSegmentData = val;
    this.update({comparisonData: this.createData(val, true)});
  }

  get comparisonSegmentData(): FunnelSegment[] {
    return this._comparisonSegmentData;
  }

  set getCohortNameAndDescription(fn) {
    this._getCohortNameAndDescription = fn;
  }

  get stepIndex() {
    return this.attr(`step-index`) || 0;
  }

  // Wrapper to more neatly toggle between strings based on conversion type
  conversionMode() {
    return conversionMode(this.attr(`chart-view-type`));
  }

  getCohortDetails({type, conversionRange, segment, eventIndex}) {
    return this._getCohortNameAndDescription({type, conversionRange, segment, eventIndex});
  }

  isConversion(): boolean {
    return this.attr(`chart-view-type`) === FrequencyViewType.Conversion;
  }

  isConversionRate(): boolean {
    return this.attr(`chart-view-type`) === FrequencyViewType.ConversionRate;
  }
}

customElements.define(`mp-funnel-frequency-chart`, FunnelsFrequencyChart);
